/** Pure derivations for the staff home (counts, tones, ordering). No I/O; unit-tested. */
import type { Tone } from '@qm/ui';
import type { Flag } from '@qm/contracts/flags';
import type { ApplicationView, DirectTenantRow, TenantRow } from '../api';
import { isMaskedTenant } from '../api';

export interface TenantStats { loaded: number; active: number; suspendedAutomation: number; partnerClients: number }

/** Counts over the rows the directory returned (one page). The caller flags "at least" when more pages exist. */
export function tenantStats(rows: readonly TenantRow[]): TenantStats {
  let active = 0;
  let suspendedAutomation = 0;
  let partnerClients = 0;
  for (const r of rows) {
    if (r.status === 'active') active += 1;
    if (isMaskedTenant(r)) partnerClients += 1;
    else if (r.automation === 'suspended') suspendedAutomation += 1;
  }
  return { loaded: rows.length, active, suspendedAutomation, partnerClients };
}

/** "38" when the list is complete, "38+" when the server reported another page. */
export const countLabel = (n: number, hasMore: boolean): string => (hasMore ? `${n}+` : String(n));

export function tenantTone(status: string): Tone {
  if (status === 'active') return 'mint';
  if (status === 'suspended' || status === 'disabled' || status === 'canceled') return 'coral';
  return 'sun';
}

export interface PendingApplications { list: ApplicationView[]; ready: number }
/** Pending applications, newest first; `ready` counts those whose vetting checklist was marked ready for a decision. */
export function pendingApplications(rows: readonly ApplicationView[]): PendingApplications {
  const list = rows.filter((a) => a.status === 'pending').sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
  return { list, ready: list.filter((a) => !!a.readyAt).length };
}

export type FlagState = 'killed' | 'on' | 'off';
export const flagState = (f: Pick<Flag, 'killSwitch' | 'defaultEnabled'>): FlagState => (f.killSwitch ? 'killed' : f.defaultEnabled ? 'on' : 'off');
export const FLAG_TONE: Record<FlagState, Tone> = { killed: 'coral', on: 'mint', off: 'lavender' };

export function flagsSummary(rows: readonly Flag[]): { total: number; killed: number; on: number } {
  return { total: rows.length, killed: rows.filter((f) => f.killSwitch).length, on: rows.filter((f) => flagState(f) === 'on').length };
}
/** Killed flags first, then by key, so the home surfaces what needs attention. */
export const flagsForHome = (rows: readonly Flag[], n: number): Flag[] =>
  [...rows].sort((a, b) => Number(b.killSwitch) - Number(a.killSwitch) || a.key.localeCompare(b.key)).slice(0, n);

export type Priority = { kind: 'applications'; count: number } | { kind: 'suspended'; count: number } | { kind: 'clear' };
/** The one thing to do first: applications ready for a decision, else tenants whose automation is suspended. */
export function homePriority(ready: number | null, suspended: number | null): Priority {
  if (ready && ready > 0) return { kind: 'applications', count: ready };
  if (suspended && suspended > 0) return { kind: 'suspended', count: suspended };
  return { kind: 'clear' };
}

/** Greeting bucket for the overview title (hour is the viewer's local hour, 0-23). */
export type Greeting = 'morning' | 'afternoon' | 'evening';
export const greetingFor = (hour: number): Greeting => (hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening');

export interface WeekDay { iso: string; date: number; dow: number; today: boolean }
/** Monday-first week (UTC calendar days) containing `now`; `today` marks the current day. */
export function weekOf(now: Date): WeekDay[] {
  const dow = (now.getUTCDay() + 6) % 7;
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - dow);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start + i * 86_400_000);
    return { iso: d.toISOString().slice(0, 10), date: d.getUTCDate(), dow: i, today: i === dow };
  });
}

export type QueueKind = 'approval' | 'vetting' | 'automation';
export interface QueueItem { id: string; kind: QueueKind; title: string; account: string; at: string; to: string; dueToday: boolean }
const QUEUE_RANK: Record<QueueKind, number> = { approval: 0, automation: 1, vetting: 2 };
/**
 * Today's work queue from real data only: partner applications ready for a decision, tenants whose automation is
 * suspended, and applications still in vetting. Masked partner clients are never named. Newest first within a kind.
 */
export function buildWorkQueue(apps: readonly ApplicationView[] | null, tenants: readonly TenantRow[]): QueueItem[] {
  const items: QueueItem[] = [];
  for (const a of apps ? pendingApplications(apps).list : []) {
    items.push({ id: `app:${a.id}`, kind: a.readyAt ? 'approval' : 'vetting', title: a.companyName, account: a.country, at: a.readyAt ?? a.createdAt, to: `/partners/${encodeURIComponent(a.id)}`, dueToday: !!a.readyAt });
  }
  for (const r of tenants) {
    if (!isMaskedTenant(r) && r.automation === 'suspended') items.push({ id: `tenant:${r.id}`, kind: 'automation', title: r.name, account: r.plan ?? '', at: r.createdAt, to: `/tenants/${encodeURIComponent(r.id)}`, dueToday: true });
  }
  return items.sort((a, b) => QUEUE_RANK[a.kind] - QUEUE_RANK[b.kind] || (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}

/** "09:30" (UTC) from an ISO instant; empty when unparseable. */
export function clockOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/** First letter(s) for a customer avatar: up to two initials of the first two words, upper-cased. */
export function initialsOf(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words.slice(0, 2).map((w) => Array.from(w)[0]!).join('').toUpperCase();
}
const AVATAR_TONES = ['lavender', 'coral', 'sun', 'sky', 'mint'] as const;
/** Stable avatar tone per name (same customer, same colour on every screen). */
export function avatarTone(seed: string): (typeof AVATAR_TONES)[number] {
  let h = 0;
  for (const ch of seed) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length]!;
}

export type Health = 'atRisk' | 'watch' | 'healthy';
/** Health derived from what the directory knows: suspended/disabled or paused automation = at risk, other non-active = watch. */
export function tenantHealth(r: Pick<DirectTenantRow, 'status' | 'automation'>): Health {
  if (r.status === 'suspended' || r.status === 'disabled' || r.status === 'canceled' || r.automation === 'suspended') return 'atRisk';
  return r.status === 'active' ? 'healthy' : 'watch';
}
export interface HealthSummary { atRisk: number; watch: number; healthy: number; total: number }
export function healthSummary(rows: readonly TenantRow[]): HealthSummary {
  const s: HealthSummary = { atRisk: 0, watch: 0, healthy: 0, total: 0 };
  for (const r of rows) {
    if (isMaskedTenant(r)) continue;
    s[tenantHealth(r)] += 1;
    s.total += 1;
  }
  return s;
}
